/**
 * benchHeatReportUtils.js
 * Generates standalone, print-ready, beautifully styled Executive HTML reports
 * for Bench Heat Report matching the high-contrast executive theme.
 */

export function triggerHtmlDownload(htmlContent, fileName) {
  const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function generateBenchHeatReportHtml({
  asOfDate,
  kpiSummary = {},
  benchAging = [],
  recruiterScorecard = [],
  slaDeadlines = [],
  filterInfo = "All Time"
}) {
  const activeCount = kpiSummary.totalActiveConsultants ?? benchAging.length ?? 0;
  const consultants45 = kpiSummary.consultants45Plus ?? 0;
  const submissionsCount = kpiSummary.submissionsCount ?? 0;
  const placementsCount = kpiSummary.placementsCount ?? 0;

  const agingRowsHtml = benchAging.map((c, i) => {
    const isCritical = c.status === 'CRITICAL' || c.daysOnBench >= 46;
    const rowClass = isCritical ? 'class="flagrow"' : '';
    const pillClass = c.statusClass || (c.status === 'CRITICAL' ? 'red' : (c.status === 'HIGH RISK' || c.status === 'HOT') ? 'orange' : (c.status === 'MODERATE' || c.status === 'WARMING') ? 'yellow' : 'green');
    const escalateBadge = c.escalate || isCritical ? '<span class="flagbadge">ESCALATE</span>' : '';

    const lastSubText = c.lastSubmissionDateFormatted || (c.lastSubmission && c.lastSubmission !== 'No Submissions' ? c.lastSubmission : '—');
    const relativeText = c.lastSubmissionRelative || (c.daysSinceLastSubmission !== undefined && c.daysSinceLastSubmission !== null ? (c.daysSinceLastSubmission === 0 ? 'Today' : c.daysSinceLastSubmission === 1 ? '1 day ago' : `${c.daysSinceLastSubmission} days ago`) : '');

    return `
      <tr ${rowClass}>
        <td style="font-weight: 600; color: #64748b; width: 40px;">${i + 1}</td>
        <td class="name" style="font-weight: 700; color: #0f172a;">${c.name || 'Unknown'}</td>
        <td class="mono muted">${c.email || '—'}</td>
        <td class="mono muted">${c.phone || '—'}</td>
        <td><span style="font-weight: 600; color: #334155;">${c.owner || 'Unassigned'}</span></td>
        <td class="mono" style="font-weight: 800; font-size: 14px; color: ${isCritical ? '#dc2626' : '#0f172a'};">${c.daysOnBench || 0}</td>
        <td class="mono muted">
          <div style="font-weight: 600; color: #334155;">${lastSubText}</div>
          ${relativeText && lastSubText !== '—' ? `<div style="font-size: 11px; color: #64748b; font-weight: 500; margin-top: 2px;">${relativeText}</div>` : ''}
        </td>
        <td class="mono" style="text-align: center; font-weight: 800; color: #0f766e; font-size: 14px;">${c.submissionCount !== undefined && c.submissionCount !== null ? c.submissionCount : (c.lifetimeSubmissions || 0)}</td>
        <td><span class="pill ${pillClass}">${c.status || 'ACTIVE'}</span>${escalateBadge}</td>
      </tr>
    `;
  }).join('');

  const scorecardRowsHtml = recruiterScorecard.map((r, idx) => {
    const rankClass = r.rank === 1 ? 'r1' : r.rank === 2 ? 'r2' : r.rank === 3 ? 'r3' : 'other';

    return `
      <tr>
        <td><span class="rank ${rankClass}">${r.rank || idx + 1}</span></td>
        <td class="name" style="font-weight: 700; color: #0f172a;">${r.recruiter || 'Recruiter'}</td>
        <td class="mono" style="font-weight: 700; color: #0f766e; font-size: 14px;">${r.submissions || 0}</td>
        <td class="mono" style="font-weight: 700; color: #2563eb; font-size: 14px;">${r.interviews || 0}</td>
        <td class="mono" style="color: #059669; font-weight: 800; font-size: 14px;">${r.placements || 0}</td>
      </tr>
    `;
  }).join('');

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>Bench Heat &amp; Aging Report — Prophecy Technologies</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
    background: #f8fafc;
    color: #1e293b;
    padding: 32px;
    line-height: 1.5;
    -webkit-font-smoothing: antialiased;
  }
  .report-container {
    max-width: 1200px;
    margin: 0 auto;
    background: #ffffff;
    border-radius: 12px;
    padding: 32px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -2px rgba(0, 0, 0, 0.05);
    border: 1px solid #e2e8f0;
  }
  .header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    border-bottom: 2px solid #3b5bfd;
    padding-bottom: 20px;
    margin-bottom: 24px;
  }
  .brand-title {
    font-size: 24px;
    font-weight: 800;
    color: #0f172a;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .brand-title span {
    color: #3b5bfd;
  }
  .meta-info {
    font-size: 13px;
    color: #64748b;
    margin-top: 4px;
  }
  .btn-group {
    display: flex;
    gap: 8px;
  }
  .download-pdf-btn, .print-btn {
    background: #3b5bfd;
    color: #ffffff;
    border: none;
    padding: 8px 18px;
    border-radius: 6px;
    font-weight: 600;
    font-size: 13px;
    cursor: pointer;
    box-shadow: 0 1px 2px rgba(0,0,0,0.1);
    transition: background 0.2s, opacity 0.2s;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .download-pdf-btn:hover, .print-btn:hover {
    background: #0f766e;
  }
  .btn-secondary {
    background: #f1f5f9;
    color: #334155;
    border: 1px solid #cbd5e1;
  }
  .btn-secondary:hover {
    background: #e2e8f0;
  }

  .filters-banner {
    background: #f1f5f9;
    padding: 12px 16px;
    border-radius: 8px;
    margin-bottom: 24px;
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }
  .filter-label {
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #475569;
  }
  .badge {
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }
  .badge.date    { background: #ffffff; color: #1e293b; border: 1px solid #cbd5e1; }
  .badge.status  { background: #ecfdf5; color: #047857; border: 1px solid #a7f3d0; }

  /* Top KPI Cards */
  .kpi-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
    margin-bottom: 32px;
  }
  .kpi-card {
    background: #ffffff;
    border-radius: 10px;
    padding: 18px 20px;
    border: 1px solid #e2e8f0;
    border-top: 4px solid #cbd5e1;
  }
  .kpi-card.teal   { border-top-color: #3b5bfd; background: #f0fdfb; }
  .kpi-card.red    { border-top-color: #ef4444; background: #fef2f2; }
  .kpi-card.blue   { border-top-color: #3b82f6; background: #eff6ff; }
  .kpi-card.green  { border-top-color: #10b981; background: #ecfdf5; }

  .kpi-title {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 6px;
  }
  .kpi-card.teal   .kpi-title { color: #0f766e; }
  .kpi-card.red    .kpi-title { color: #b91c1c; }
  .kpi-card.blue   .kpi-title { color: #1d4ed8; }
  .kpi-card.green  .kpi-title { color: #047857; }

  .kpi-value {
    font-size: 28px;
    font-weight: 800;
    color: #0f172a;
    line-height: 1.1;
  }
  .kpi-card.red .kpi-value { color: #dc2626; }
  .kpi-sub {
    font-size: 11.5px;
    color: #64748b;
    margin-top: 4px;
    font-weight: 500;
  }

  section { margin-bottom: 32px; }
  .section-title {
    font-size: 16px;
    font-weight: 700;
    color: #0f172a;
    margin-bottom: 12px;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .section-count {
    font-size: 12px;
    background: #f1f5f9;
    color: #475569;
    padding: 2px 8px;
    border-radius: 4px;
    font-weight: 600;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    overflow: hidden;
    font-size: 13px;
  }
  th {
    text-align: left;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: .04em;
    color: #475569;
    font-weight: 700;
    padding: 12px 14px;
    border-bottom: 2px solid #e2e8f0;
    background: #f8fafc;
  }
  td {
    padding: 12px 14px;
    border-bottom: 1px solid #f1f5f9;
    vertical-align: middle;
    color: #1e293b;
  }
  tr:last-child td { border-bottom: none; }
  tr:hover td { background: #f8fafc; }

  .mono { font-family: 'JetBrains Mono', Consolas, monospace; font-size: 12px; }
  .muted { color: #64748b; }

  .pill {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 10px;
    border-radius: 20px;
    font-size: 11.5px;
    font-weight: 700;
    letter-spacing: 0.02em;
  }
  .pill.green  { background: #ecfdf5; color: #047857; border: 1px solid #a7f3d0; }
  .pill.yellow { background: #fffbeb; color: #b45309; border: 1px solid #fde68a; }
  .pill.orange { background: #fff7ed; color: #c2410c; border: 1px solid #ffedd5; }
  .pill.red    { background: #fef2f2; color: #b91c1c; border: 1px solid #fecaca; }

  .rank {
    width: 26px;
    height: 26px;
    border-radius: 6px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 12px;
  }
  .rank.r1 { background: #fef3c7; color: #d97706; border: 1px solid #fde68a; }
  .rank.r2 { background: #f1f5f9; color: #475569; border: 1px solid #cbd5e1; }
  .rank.r3 { background: #fff7ed; color: #c2410c; border: 1px solid #ffedd5; }
  .rank.other { background: #f8fafc; color: #64748b; }

  .flagrow td { background: #fff5f5 !important; }
  .flagbadge {
    font-size: 10px;
    background: #ef4444;
    color: #ffffff;
    padding: 2px 6px;
    border-radius: 4px;
    margin-left: 6px;
    font-weight: 700;
    letter-spacing: .03em;
  }

  .footer {
    margin-top: 24px;
    padding-top: 14px;
    border-top: 1px solid #e2e8f0;
    display: flex;
    justify-content: space-between;
    font-size: 12px;
    color: #64748b;
  }

  @media print {
    body { background: #ffffff !important; padding: 0 !important; }
    .report-container { border: none !important; box-shadow: none !important; padding: 0 !important; max-width: 100% !important; }
    .header button, .btn-group { display: none !important; }
  }
</style>
</head>
<body>

<div class="report-container">
  <div class="header">
    <div>
      <h1 class="brand-title">Prophecy <span>ERP</span> &mdash; Bench Heat &amp; Aging Report</h1>
      <p class="meta-info">Delivery &amp; Bench Sales &nbsp;|&nbsp; Generated: <strong>${new Date().toLocaleString()}</strong></p>
    </div>
    <div class="btn-group">
      <button class="download-pdf-btn" id="downloadPdfBtn" onclick="downloadAsPDF()">📥 Download as PDF</button>
      <button class="download-pdf-btn btn-secondary" onclick="window.print()">🖨️ Print</button>
    </div>
  </div>

  <div class="filters-banner">
    <span class="filter-label">Active Filters:</span>
    <span class="badge date">📅 ${filterInfo}</span>
    <span class="badge status">⚡ Live Bench Sync</span>
  </div>

  <div class="kpi-grid">
    <div class="kpi-card teal">
      <div class="kpi-title">Active Bench Candidates</div>
      <div class="kpi-value">${activeCount}</div>
      <div class="kpi-sub">Total active consultants</div>
    </div>
    <div class="kpi-card red">
      <div class="kpi-title">Consultants 45+ Days</div>
      <div class="kpi-value">${consultants45}</div>
      <div class="kpi-sub">High aging escalation</div>
    </div>
    <div class="kpi-card blue">
      <div class="kpi-title">Submissions</div>
      <div class="kpi-value">${submissionsCount}</div>
      <div class="kpi-sub">Profiles submitted</div>
    </div>
    <div class="kpi-card green">
      <div class="kpi-title">Placements</div>
      <div class="kpi-value">${placementsCount}</div>
      <div class="kpi-sub">Offers &amp; confirmed joins</div>
    </div>
  </div>

  <section>
    <h2 class="section-title">
      Bench Aging &mdash; By Consultant <span class="section-count">${activeCount} Active</span>
    </h2>
    <table>
      <thead>
        <tr>
          <th style="width: 40px;">#</th>
          <th>Consultant</th>
          <th>Email</th>
          <th>Phone</th>
          <th>Recruiter</th>
          <th>Days on Bench</th>
          <th>Last Submission</th>
          <th style="text-align: center;">Total Submissions</th>
          <th>Status</th>
        </tr>
      </thead>
      <tbody>
        ${agingRowsHtml || '<tr><td colspan="9" style="padding: 16px; text-align: center; color: #94a3b8;">No consultants on bench matching criteria</td></tr>'}
      </tbody>
    </table>
  </section>

  <section>
    <h2 class="section-title">
      Recruiter Performance &mdash; Bench Sales <span class="section-count">${recruiterScorecard.length} Recruiters</span>
    </h2>
    <table>
      <thead>
        <tr>
          <th style="width: 50px;">Rank</th>
          <th>Recruiter</th>
          <th>Submissions</th>
          <th>Interviews</th>
          <th>Placements</th>
        </tr>
      </thead>
      <tbody>
        ${scorecardRowsHtml || '<tr><td colspan="5" style="padding: 16px; text-align: center; color: #94a3b8;">No recruiter activity recorded</td></tr>'}
      </tbody>
    </table>
  </section>

  <div class="footer">
    <span>Status tiers: Active (0–15d) &bull; Moderate (16–30d) &bull; High Risk (31–45d) &bull; Critical (46+d)</span>
    <span>Prophecy ERP Confidential &bull; Page 1 of 1</span>
  </div>
</div>

<script src="https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js"></script>
<script>
  function downloadAsPDF() {
    var btn = document.getElementById('downloadPdfBtn');
    var originalHtml = btn ? btn.innerHTML : '';
    if (btn) {
      btn.innerHTML = '⏳ Generating PDF...';
      btn.disabled = true;
      btn.style.opacity = '0.7';
    }

    var element = document.querySelector('.report-container') || document.body;
    var cleanTitle = (document.title || 'Bench_Heat_Report')
      .replace(/[^a-zA-Z0-9_\\-\\s]/g, '')
      .trim()
      .replace(/\\s+/g, '_');

    var opt = {
      margin: [8, 8, 8, 8],
      filename: cleanTitle + '.pdf',
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true, logging: false },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'landscape' }
    };

    if (window.html2pdf) {
      if (btn) btn.style.display = 'none';
      html2pdf().set(opt).from(element).save().then(function() {
        if (btn) {
          btn.style.display = '';
          btn.innerHTML = originalHtml;
          btn.disabled = false;
          btn.style.opacity = '1';
        }
      }).catch(function(err) {
        console.error('PDF error:', err);
        if (btn) {
          btn.style.display = '';
          btn.innerHTML = originalHtml;
          btn.disabled = false;
          btn.style.opacity = '1';
        }
        window.print();
      });
    } else {
      window.print();
      if (btn) {
        btn.innerHTML = originalHtml;
        btn.disabled = false;
        btn.style.opacity = '1';
      }
    }
  }
</script>

</body>
</html>
`;
}
